/* eslint-disable no-undef */
/* 此文件已转为门面，仅为兼容旧版本G插件，不应再进行更新。
   新接口请添加到desktopUtils.js, 并通过Vue.prototype.$desktopClient使用。

   此文件中的门面接口，可保持项目中已存在的对此文件导出接口的调用依然有效：
   对于旧的G插件：会通过gcsUtilsLegacy.js保持旧的调用。
   对于新的G插件：会通过gcsUtilsAdapter.js适配到新的调用（desktopUtils.js）。
   参见：https://gjw-prod-static.obs.cn-north-4.myhuaweicloud.com/docs/gcsUtilsCompatible_gjw-selection-frontend.png
*/

import * as legacy from './gcsUtilsLegacy'
import * as adapter from './gcsUtilsAdapter'

let forceLegacy = false

function setForceLegacy(value) {
  forceLegacy = value
}

function newEra() {
  if (forceLegacy === true) {
    return false
  }
  return typeof CefSharp !== 'undefined'
}

function chooseClient() {
  if (newEra()) {
    return adapter
  } else {
    return legacy
  }
}

/*
@description 初始化
@   1、注册Listener，以监听C++对JS的调用
@params void
@
@return void
*/
function Init() {
  chooseClient().Init()
}

/*
@description 调用C++，获取access token
@note 由于历史原因，函数名称中包含Gdc，但实际是获取插件所记录的用户信息中的accessToken，不限于Gdc
@params void
@
@return a Promise. resolve 结果为:
  {
      code: String,
      message: String,
      data: String
  }
  code - 为success表示成功
  data - access token
*/
async function GetGdcAccessToken() {
  return await chooseClient().GetGdcAccessToken()
}

/*
@description 调用C++，获取当前协同项目信息
@params void
@
@return a Promise. resolve 结果为:
    {
        code: String,
        message: String,
        data: <ProjectInfo>
    }
    code - 为success表示成功
    data - 当前协同项目信息对象，其结构为：
    {
        id: String,
        name: String,
        path: String
    }
*/
async function GetProjectInfo() {
  return await chooseClient().GetProjectInfo()
}

/*
@description 调用C++，获取协同http服务终结点
@params 终结点的key，对于协同项目构件服务，其终结点key为structure
@
@return a Promise. resolve 结果为：
  {
      code: String,
      message: String,
      data: String
  }
  code - 为success表示成功
  data - 所指定key的协同http服务终结点
*/
async function GetGdcEndpoint(key) {
  return await chooseClient().GetGdcEndpoint(key)
}

/*
@description 调用C++，发送埋点
@params event 事件名称
@params params 事件参数
@
@return a Promise. resolve 结果为：
  {
      code: String,
      message: String,
      data: bool
  }
  code - 为success表示成功
  data - Track的结果，true/false
*/
async function Track(eventName, eventParams) {
  return await chooseClient().Track(eventName, eventParams)
}

/*
@description 调用C++，打开默认浏览器跳转到指定链接
@params targetUrl 要打开的目标链接
@
@return a Promise. resolve 结果为：
  {
      code: String,
      message: String,
      data: bool
  }
  code - 为success表示成功
  data - NavigateToBrowser的结果，true/false
*/
async function NavigateToBrowser(targetUrl) {
  return await chooseClient().NavigateToBrowser(targetUrl)
}

/*
@description 调用C++，获取协同当前环境
@params void
@
@return a Promise. resolve 结果为:
    {
        code: String,
        message: String,
        data: String
    }
    code - 为success表示成功
    data - 协同当前环境名称
*/
async function GetGdcEnvironment() {
  return await chooseClient().GetGdcEnvironment()
}
/*
@description 调用C++，获取Gcs插件相关信息
@params void
@
@return a Promise. resolve 结果为:
    {
        code: String,
        message: String,
        data: <GcsAddinInfo>
    }
    code - 为success表示成功
    data - Gcs插件信息对象，其结构为：
    {
        edition: String  // 插件版别: collaboration - 协同版, standalone - 单机版
    }
*/
async function GetGcsAddinInfo() {
  return await chooseClient().GetGcsAddinInfo()
}

/*
@description 调用C++，获取机电软件传递过来的设备选型参数
@params void
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: Array
    }
    code - 为success表示成功
    data - 机电软件传递过来的选型参数，其结构按约定，详见：https://km.glodon.com/x/CTxLIQ
*/
async function GetEquipmentSelectionArguments() {
  return await chooseClient().GetEquipmentSelectionArguments()
}
/*
@description 调用C++，设置设备选型结果，以回传给机电
@params result:Object 要回传给机电软件的设备选型结果，其结构按约定，详见：https://km.glodon.com/x/CTxLIQ
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: bool
    }
    code - 为success表示成功
    data - SetEquipmentSelectionResult的结果，true/false
*/
async function SetEquipmentSelectionResult(result) {
  return await chooseClient().SetEquipmentSelectionResult(result)
}
/*
@description 将列表/卡片构件数据转为载入、布置、批量载入共用的 publicComponent 入参
@params comp 列表项构件
@return {
    id: String,
    digitalComponentId: String,
    name: String,
    suffix: String,
    categoryNames: array<String>,
    thumbnail: String,
    softwareCategoryCodes: array<String>,
    softwareVersion: String,
    storeId: Integer(int64),
}
*/
function buildPublicComponentPayload(comp) {
  return {
    id: String(comp.publicComponentId),
    digitalComponentId: String(comp.coreResourceVersionId),
    name: comp.name,
    suffix: comp.suffix,
    categoryNames: comp.softwareCategoryNames || [],
    thumbnail: comp.thumbnail,
    softwareCategoryCodes: comp.softwareCategoryCodes || [],
    softwareVersion: comp.softwareVersion,
    storeId: Number(comp.storeId),
  }
}

/*
@description 调用C++，载入公共构件
@params publicComponent 要载入的公共构件对象，其结构为：
    {
        id: String,
        digitalComponentId: String,
        name: String,
        suffix: String,
        categoryNames: array<String>,
        thumbnail: String,
        softwareCategoryCodes: array<String>,
        softwareVersion: String,
        storeId : Integer(int64),
    }
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: bool
    }
    code - 为success表示成功
    data - LoadPublicComponent的结果，true/false
*/
async function LoadPublicComponent(publicComponent) {
  return await chooseClient().LoadPublicComponent(publicComponent)
}
/*
@description 调用C++，批量载入公共构件
@params publicComponents 要载入的公共构件对象列表，其结构为：
    [
        {
            id: String,
            digitalComponentId: String,
            name: String,
            suffix: String,
            categoryNames: array<String>,
            thumbnail: String,
            softwareCategoryCodes: array<String>,
            softwareVersion: String,
            storeId : Integer(int64),
        }
    ]
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: array<object>
    }
    code - 为success表示成功
    data - LoadPublicComponentInBatch的结果，其结构为：
    [
        {
          "publicComponentId": String,      // 公共构件id
          "loadResult": String,             // 载入结果：SUCCESS - 成功，Failure - 失败， SKIPPED - 跳过
        }
    ]
*/
async function LoadPublicComponentInBatch(publicComponents) {
  return await chooseClient().LoadPublicComponentInBatch(publicComponents)
}
/*
@description 调用C++，布置公共构件
@params publicComponent 要布置的公共构件对象，其结构为：
    {
        id: String,
        digitalComponentId: String,
        name: String,
        suffix: String,
        categoryNames: array<String>,
        thumbnail: String,
        softwareCategoryCodes: array<String>,
        softwareVersion: String,
        storeId : Integer(int64),
        instanceParameters: [
          {
            parameterCode: String, 参数编码
            guid: String, 参数GUID
            name: String, 参数名称
            value: String, 参数值
            unitCode: String, 参数单位编码
            unit: String 参数单位符号
          }
        ],
    }
@params familyTypeToBePlaced 要布置的构件类型名称
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: bool
    }
    code - 为success表示成功
    data - PlacePublicComponent的结果，true/false
*/
async function PlacePublicComponent(publicComponent, familyTypeToBePlaced) {
  return await chooseClient().PlacePublicComponent(publicComponent, familyTypeToBePlaced)
}

/*
@description 调用C++，检查公共构件是否已载入（添加）
@params publicComponents 要检查的公共构件对象数组，其结构为：
    [
        {
            id: String,
            name: String
        }
    ]
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: array<Object>
    }
    code - 为success表示成功
    data - CheckPublicComponentLoaded的结果，其结构为：
    [
        {
          "publicComponentId": String,      // 公共构件id
          "isLoaded": bool,                 // 是否已载入（添加）
        }
    ]
*/
async function CheckPublicComponentLoaded(publicComponents) {
  return await chooseClient().CheckPublicComponentLoaded(publicComponents)
}

/*
@description 调用C++，载入材料
@params LoadMaterial 要载入的材料对象，其结构为：
    {
        id: String,
        name: String,
        gjwCategoryCode: String,
        gjwCategoryName: String,
        dataSource: String,
        materialPropertyValues:[
            {
                code: String,
                name: String,
                type: String,
                value: String
            }
        ]
    }
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: bool
    }
    code - 为success表示成功
    data - LoadMaterial的结果，true/false
*/
async function LoadMaterial(material) {
  return await chooseClient().LoadMaterial(material)
}

/*
@description 调用C++，获取价格查询窗口询价条件（仅在价格查询窗口内可用）
@params void
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: Object
    }
    code - 为success表示成功
    data - 价格查询窗口询价条件，其结构为：
    {
      "type": String,           // 资源类型，可用值:MATERIAL, PRODUCT
      "areaName": String,       // 地区名称(省/直辖市、市之间使用-连接)
      "name": String,           // 材料/产品名称
      "thickness": String,      // 材料厚度
      "categoryCode": String,   // 分类编码
      "categoryName": String,   // 分类名称
      "categoryProperties": [   // 属性值列表
        {
          "code": String,       // 属性编码
          "value": String       // 属性值
        }
      ]
    }
*/
async function GetPriceEnquiryWindowOptions() {
  return await chooseClient().GetPriceEnquiryWindowOptions()
}

/*
@description 调用C++，获取材料询价条件
@params void
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: Object
    }
    code - 为success表示成功
    data - 材料询价条件，其结构为：
    {
        areaName: String,   // 地区名称(省/直辖市、市之间使用-连接)
        thickness: String,  // 材料厚度
        ... ...
    }
*/
async function GetMaterialPriceEnquiryHint() {
  return await chooseClient().GetMaterialPriceEnquiryHint()
}
/*
@description 调用C++，获取产品询价条件
@params void
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: Object
    }
    code - 为success表示成功
    data - 产品询价条件，其结构为：
    {
        areaName: String,  // 地区名称(省/直辖市、市之间使用-连接)
        ... ...
    }
*/
async function GetProductPriceEnquiryHint() {
  return await chooseClient().GetProductPriceEnquiryHint()
}
/*
@description 调用C++，载入模块
@params module 要载入的模块对象，其结构为：
    {
        functionType: String,
        guid: String,
        id: Integer(int64),
        metaJson: String,
        moduleFileName: String,
        name: String,
        softwareCode: String,
        softwareVersion: String,
        specialtyType: String,
        suffix: String,
        type: String,
        moduleFileDownloader:
        {
            url: String,
            redirect: true,
            auth: true
        }
    }
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: bool
    }
    code - 为success表示成功
    data - LoadModule 的结果，true/false
*/
async function LoadModule(module) {
  return await chooseClient().LoadModule(module)
}
/*
@description 调用C++，载入工程做法
@params practice 要载入的工程做法对象，其结构为：
    {
        categoryCode: String,
        categoryName: String,
        constructionLayers: [
            {
                constructionLayerFunction: String,
                constructionLayerMaterial: String,
                description: String,
                id: Integer(int64),
                material: {
                    id: Integer(int64),
                    name: String,
                    gjwCategoryCode: String,
                    gjwCategoryName: String,
                    dataSource: String,
                    materialPropertyValues:[
                        {
                            code: String,
                            name: String,
                            type: String,
                            value: String
                        }
                    ]
                },
                number: Integer(int32),
                thick: double,
                thickUnit: String
            }
        ]
        dataSource: String,
        id: Integer(int64),
        practiceFeature: String,
        practiceName: String,
        practiceThick: double,
        waterproofHeight: double
    }
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: bool
    }
    code - 为success表示成功
    data - LoadEngineeringPractice 的结果，true/false
*/
async function LoadEngineeringPractice(practice) {
  return await chooseClient().LoadEngineeringPractice(practice)
}

/*
@description 调用C++，创建一个浏览器对话框，并加载指定页面
@params targetUrl 要加载的页面地址
@params options 浏览器对话框选项，其结构为：
    {
        contentWidth: integer(int32),  // 页面宽度
        contentHeight: integer(int32),   // 页面高度
        isModal: bool,                  // 是否为模态，默认为是
        titleBar: {                     // 标题栏
            text: String,               // 标题文本
            background: String,         // 标题栏背景色, 默认为 #FFFFFF
            showCloseButton: bool,      // 是否显示关闭按钮，默认为是
            showMinimizeButton: bool,   // 是否显示最小化按钮，默认为否
            showMaximizeButton: bool    // 是否显示最大化按钮，默认为否
        }
    }
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: bool
    }
    code - 为success表示成功
    data - CreateBrowser的结果，true/false
*/
async function CreateBrowser(targetUrl, options) {
  return await chooseClient().CreateBrowser(targetUrl, options)
}
/*
@description 调用C++，关闭内嵌浏览器窗口（关闭当前调用所在的浏览器窗口）
@params void
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: bool
    }
    code - 为success表示成功
    data - 关闭内嵌浏览器窗口的结果，true/false
*/
async function CloseBrowser() {
  return await chooseClient().CloseBrowser()
}

/*
@description 调用C++，移动内嵌浏览器窗口
@params void
@note 在页面上，需要通过鼠标拖拽实现窗口移动的部位，在onmousedown事件中调用此方法
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: bool
    }
    code - 为success表示成功
    data - 移动内嵌浏览器窗口的结果，true/false
*/
async function MoveBrowser() {
  return await chooseClient().MoveBrowser()
}
/*
@description 调用C++，保存用户认证数据
@params userAuthorizationData 用户认证数据，其结构为：
    {
        id: String,                 // 用户ID
        name: String,               // 用户名
        accessToken: String,        // 用户访问令牌
    }
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: bool
    }
    code - 为success表示成功
    data - SaveUserAuthorizationData的结果，true/false
*/
async function SaveUserAuthorizationData(userAuthorizationData) {
  return await chooseClient().SaveUserAuthorizationData(userAuthorizationData)
}
/*
@description 调用C++，录制日志脚本
@params actionName : String 要录制的动作名称
@params actionArgs : Object 要录制的动作参数
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: bool
    }
    code - 为success表示成功
    data - RecordJournal的结果，true/false
*/
async function RecordJournal(actionName, actionArgs) {
  return await chooseClient().RecordJournal(actionName, actionArgs)
}
/*
@description 调用C++，通知日志脚本回放完成
@params actionName : String 回放完成的动作名称
@params actionResult : Object 动作回放完成的结果，其结构为：
    {
        code: String,
        message: String,
        data: bool
    }
    code - action回放的结果编码。若action回放成功，则为success；否则，为所约定的错误码
    message - action回放的结果解释。若action回放成功，则为空；否则，为失败的原因描述
    data - action回放的结果数据。一般情况可设为bool值，true/false；若需传递其他结果数据，可具体设置
@
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: bool
    }
    code - 为success表示成功
    data - CompleteReplayJournal的结果，true/false
*/
async function CompleteReplayJournal(actionName, actionResult) {
  return await chooseClient().CompleteReplayJournal(actionName, actionResult)
}
/*
@description 调用C++，通知日志脚本回放预检结果
@params actionName : String 回放预检的动作名称
@params precheckResult : Object 回放预检的结果，其结构为：
    {
        isReady: bool
    }
    isReady - 若已具备回放条件，则预检通过，设为true；否则，设为false
@
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: bool
    }
    code - 为success表示成功
    data - ConfirmReplayJournalPrecheck的结果，true/false
*/
async function ConfirmReplayJournalPrecheck(actionName, precheckResult) {
  return await chooseClient().ConfirmReplayJournalPrecheck(actionName, precheckResult)
}
/*
@description 调用C++，获取机电软件传递过来的设备批量选型参数
@params void
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: Array
    }
    code - 为success表示成功
    data - 机电软件传递过来的批量选型参数，其结构按约定，详见：https://km.glodon.com/x/KBMqHg
*/
async function GetEquipmentBatchSelectionArguments() {
  return await chooseClient().GetEquipmentBatchSelectionArguments()
}
/*
@description 调用C++，设置设备批量选型结果，以回传给机电
@params material 要回传给机电软件的设备批量选型结果，其结构按约定，详见：https://km.glodon.com/x/KBMqHg
@
@return a Promise. resolve 结果为：
    {
        code: String,
        message: String,
        data: bool
    }
    code - 为success表示成功
    data - SetEquipmentBatchSelectionResults的结果，true/false
*/
async function SetEquipmentBatchSelectionResults(results) {
  return await chooseClient().SetEquipmentBatchSelectionResults(results)
}
/*
@description 注册一个处理来自Gcs插件的调用的JS处理器
@params handlerObj 要注册的处理器对象，格式为:
                id:处理器的id(防止前端push进来同一个handler)
@               handler:(method, params) => Promise(resolve => bool)
@               其中：
@                  method: 要调用的JS方法名
@                  params: 要调用的JS方法参数对象
@                  返回值：Promise, resolve的结果是一个bool值，若已处理(Handled)，则为true；否则，为false
@
@return void
*/
function RegisterGcsPluginCallingHandler(handlerObj) {
  chooseClient().RegisterGcsPluginCallingHandler(handlerObj)
}

export {
  GetEquipmentBatchSelectionArguments,
  SetEquipmentBatchSelectionResults,
  GetGdcAccessToken,
  GetProjectInfo,
  GetGdcEndpoint,
  Track,
  NavigateToBrowser,
  GetGdcEnvironment,
  GetGcsAddinInfo,
  GetEquipmentSelectionArguments,
  SetEquipmentSelectionResult,
  buildPublicComponentPayload,
  LoadPublicComponent,
  LoadPublicComponentInBatch,
  PlacePublicComponent,
  CheckPublicComponentLoaded,
  LoadMaterial,
  GetPriceEnquiryWindowOptions,
  GetMaterialPriceEnquiryHint,
  GetProductPriceEnquiryHint,
  LoadModule,
  LoadEngineeringPractice,
  CreateBrowser,
  CloseBrowser,
  MoveBrowser,
  SaveUserAuthorizationData,
  RecordJournal,
  RegisterGcsPluginCallingHandler,
  CompleteReplayJournal,
  ConfirmReplayJournalPrecheck,
  Init,
  setForceLegacy
}
